import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { companyApi, companyQueryKeys } from "../api/company";
import {
  Company,
  CreateCompanyPayload,
  GetCompaniesParams,
} from "types/crm/company";
import toast from "react-hot-toast";

/**
 * Fetches companies for the org, filtered by the current search term.
 * orgUuid is only used for query-key scoping / the `enabled` guard - the
 * request itself relies on the org context the API already resolves from
 * auth (same convention as useContactOwners / getContactOwners).
 */
export const useCompanies = (
  orgUuid: string,
  params: GetCompaniesParams,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: companyQueryKeys.list(params),
    queryFn: () => companyApi.getCompanies(params),
    enabled: !!orgUuid && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
};

export const useCompaniesInfinite = (
  orgUuid: string,
  params: Omit<GetCompaniesParams, "page"> = {},
  options?: { enabled?: boolean },
) => {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: companyQueryKeys.infiniteList(params),
    queryFn: ({ pageParam }) =>
      companyApi.getCompaniesPage({ ...params, page: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.last ? undefined : lastPage.page + 1,
    enabled: !!orgUuid && (options?.enabled ?? true),
  });

  return {
    companies: data?.pages.flatMap((p) => p?.content ?? []) ?? [],
    isLoading,
    isFetchingMore: isFetchingNextPage,
    hasMore: !!hasNextPage,
    onLoadMore: fetchNextPage,
    totalElements: data?.pages[0]?.totalElements ?? 0,
  };
};
// export const useCreateCompanyMutation = () => {
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: (payload: CreateCompanyPayload): Promise<Company> =>
//       companyApi.createCompany(payload),
//     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: companyQueryKeys.all });
//     },
//   });
// };

// TODO: handle all toast on component level

export const useCreateCompanyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateCompanyPayload): Promise<Company> =>
      companyApi.createCompany(payload),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: companyQueryKeys.all,
      });
    },

    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to create company.",
      );
    },
  });
};
